import * as XLSX from 'xlsx';

import {
  FileParser,
  JournalEntry,
  ParseResult,
  Property,
  RawCellDatum,
  TableType,
} from './types';
import {
  cellToString,
  cellToNumber,
} from './conv';
import {
  isNumericHeader,
  ParseSheetConfig,
  ParseTableConfig,
} from './config';
import { assertNever, getBalance } from './utils';
import { getTenantIdByName } from './tenant_mapping';
import { classifyAccount } from './account_classifier';

type Flow = 'credit' | 'debit';

// Parser that can be configured flexibly.
export class FlexParser implements FileParser {
  constructor(
    private readonly parseConfig: ParseSheetConfig,
    private readonly property: Property,
    private readonly reportDate: Date,
  ) {}

  parse(data: Uint8Array): ParseResult {
    const workbook = XLSX.read(data, { type: 'array' });
    const worksheet = workbook.Sheets[this.parseConfig.sheetName ?? 'Input'];

    const sums = new Map<TableType, number>();
    const results: Array<Array<JournalEntry>> = [];
    for (const table of this.parseConfig.tables) {
      const rows = XLSX.utils.sheet_to_json(
        worksheet,
        {
          header: table.headers.map((h) => h.name),
          range: table.range,
        },
      );

      // TODO: remove cast
      const tableResult = this.parseTable(table, rows as Array<Record<string, RawCellDatum>>);
      results.push(...tableResult.journalEntryGroups);

      sums.set(table.displayName, (sums.get(table.displayName) ?? 0) + tableResult.sum);
    }

    return {
      property: this.property,
      reportDate: this.reportDate,
      journalEntryGroups: results,
      sums,
    };
  }

  private parseTable(
    config: ParseTableConfig,
    rows: Array<Record<string, RawCellDatum>>,
  ): {
    journalEntryGroups: Array<Array<JournalEntry>>,
    sum: number,
  } {
    let sum = 0;
    const results: Array<Array<JournalEntry>> = [];

    let flow: Flow; 
    switch (config.displayName) {
      case '收入':
        flow = config.reverseSign ? 'debit' : 'credit';
        break;
      case '其他收入':
        flow = config.reverseSign ? 'debit' : 'credit';
        break;
      case '支出':
        flow = config.reverseSign ? 'credit' : 'debit';
        break;
      default:
        assertNever(config.displayName);
    }

    for (const row of rows) {
      // Get metadata
      const room = cellToString(row['号室']);
      const tenant = cellToString(row['契約者']);
      const tenantId: string | undefined = tenant != null ? getTenantIdByName(tenant) : undefined;

      const item = cellToString(row['項目']);

      const note = cellToString(row['備考']);
      const payer = cellToString(row['支付者']);
      const payee = cellToString(row['受取者']);

      const combinedNote = ([
        payer != null ? `From ${payer}` : null,
        payee != null ? `To ${payee}` : null,
        note,
      ]).filter((s) => s != null).join(';');

      const entries: Array<JournalEntry> = [];
      for (const col of config.headers) {
        if (row[col.name] == null) {
          continue;
        } else if (!isNumericHeader(col.name)) {
          continue;
        } else if (col.kind !== 'numeric') {
          continue;
        }

        const description = ([
          this.property,
          this.reportDate.getFullYear().toString(),
          '年',
          (this.reportDate.getMonth()+1).toString(),
          '月',
          config.displayName,
          room ?? '',
          item == null ? '' : `-${item}`,
        ]).join('');

        const amount = cellToNumber(row[col.name]);
        if (amount == null) {
          continue;
        } else if (amount === 0) {
          continue;
        }

        if (col.includeInSum) {
          switch (flow) {
            case 'credit':
              sum += amount;
              break;
            case 'debit':
              sum -= amount;
              break;
            default:
              assertNever(flow);
          }
        }

        // TODO: test negative number handling, especially for debit
        let creditAmount: number | undefined = undefined;
        let debitAmount: number | undefined = undefined;
        switch (flow) {
          case 'credit':
            if (amount > 0) {
              creditAmount = amount;
            } else {
              debitAmount = Math.abs(amount);
            }
            break;
          case 'debit':
            if (amount < 0) {
              creditAmount = Math.abs(amount);
            } else {
              debitAmount = amount;
            }
            break;
          default:
            assertNever(flow);
        }

        const classifiedAccount = classifyAccount(config.displayName, description);
        entries.push({
          date: this.reportDate,
          property: this.property,
          description,
          account: classifiedAccount != null ? classifiedAccount : col.account,
          tenant: tenantId,
          note: combinedNote === "" ? undefined : combinedNote,
          creditAmount,
          debitAmount,
        });
      }

      if (config.autoBalanceAccount != null) {
        const balance = getBalance(entries);
        if (balance !== 0) {
          entries.push({
            date: this.reportDate,
            property: this.property,
            description: "Auto Balance",
            account: config.autoBalanceAccount,
            creditAmount: balance < 0 ? Math.abs(balance) : undefined,
            debitAmount: balance > 0 ? Math.abs(balance) : undefined,
          });
        }
      }

      if (entries.length > 0) {
        // TODO: test this behavior to make sure no empty groups
        results.push(entries);
      }
    }

    if (config.singleJournalId) {
      return { journalEntryGroups: [results.flat()], sum };
    }
    return { journalEntryGroups: results, sum };
  }
}
